import { Box, Typography, styled, IconButton } from "@mui/material";
import { colors } from "../../../../theme/colors";

export const TABLE_ACTIONS_COLUMN_WIDTH = "64px";
export const TABLE_GRID_COLUMNS = `1fr 250px 130px 130px 60px ${TABLE_ACTIONS_COLUMN_WIDTH}`;
export const TABLE_GRID_COLUMNS_VIEW = `1fr 250px 130px 130px 60px ${TABLE_ACTIONS_COLUMN_WIDTH}`;

export const getTableGridColumns = (showActionsColumn = true) =>
  showActionsColumn ? TABLE_GRID_COLUMNS : TABLE_GRID_COLUMNS_VIEW;

export const AppointmentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDeleting",
})<{ isDeleting?: boolean }>(({ theme, isDeleting }) => ({
  position: "relative",
  opacity: isDeleting ? 0.7 : 1,
  pointerEvents: isDeleting ? "none" : undefined,
  transition: "opacity 0.15s ease",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    marginBottom: "8px",
  },
}));

export const AppointmentLoadingOverlay = styled(Box)({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
  borderRadius: "inherit",
});

export const AppointmentHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showActionsColumn",
})<{ showActionsColumn?: boolean }>(({ theme, showActionsColumn = true }) => ({
  position: "relative",
  display: "grid",
  gridTemplateColumns: getTableGridColumns(showActionsColumn),
  gap: "16px",
  alignItems: "flex-start",
  borderRadius: "10px",
  padding: "10px 16px",
  backgroundColor: colors.background.mediumBlue,
  [theme.breakpoints.down("sm")]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "8px",
    padding: "12px",
    borderRadius: "8px",
  },
}));

export const DragHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "-20px",
  top: "50%",
  transform: "translateY(-50%)",
  cursor: "grab",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
  "&:active": {
    cursor: "grabbing",
  },
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

export const AppointmentCdtSpacer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

export const AppointmentColumnSpacer = styled(AppointmentCdtSpacer)({});

export const AppointmentNameCell = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: "8px",
  },
}));

export const AppointmentNumber = styled(Typography)(({ theme }) => ({
  fontSize: "15px",
  fontWeight: 600,
  color: colors.text.action,
  fontFamily: "Sora",
  flexShrink: 0,
  marginRight: "10px",
  lineHeight: "22px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    marginRight: "8px",
  },
}));

export const AppointmentName = styled(Typography)(({ theme }) => ({
  fontSize: "15px",
  fontWeight: 600,
  color: colors.text.link,
  fontFamily: "Sora",
  minWidth: 0,
  flex: 1,
  marginRight: "12px",
  lineHeight: "22px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    marginRight: 0,
  },
}));

export const AppointmentDuration = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  color: colors.primary.main,
  fontFamily: "Nunito",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
  },
}));

export const AppointmentBadge = styled(Box)(({ theme }) => ({
  backgroundColor: colors.background.darkBlue,
  color: colors.background.secondary,
  padding: "1px 8px",
  borderRadius: "6px",
  fontSize: "14px",
  lineHeight: "22px",
  whiteSpace: "nowrap",
  fontWeight: 700,
  fontFamily: "Nunito",
  marginLeft: "8px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    lineHeight: "20px",
    marginLeft: 0,
  },
}));

export const AppointmentFeeRow = styled(Box)(({ theme }) => ({
  display: "contents",
  [theme.breakpoints.down("sm")]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    width: "100%",
  },
}));

export const AppointmentFieldLabel = styled(Typography)(({ theme }) => ({
  display: "none",
  fontSize: "12px",
  fontWeight: 700,
  color: colors.text.secondary,
  fontFamily: "Nunito",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    display: "block",
  },
}));

export const AppointmentFee = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  color: colors.text.primary,
  fontFamily: "Nunito",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    textAlign: "right",
  },
}));

export const AppointmentActions = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-end",
  gap: "4px",
  width: TABLE_ACTIONS_COLUMN_WIDTH,
  minWidth: TABLE_ACTIONS_COLUMN_WIDTH,
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: "auto",
    minWidth: 0,
    alignSelf: "flex-end",
  },
}));

export const TableActionsPlaceholder = styled(Box)(({ theme }) => ({
  width: TABLE_ACTIONS_COLUMN_WIDTH,
  minWidth: TABLE_ACTIONS_COLUMN_WIDTH,
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

export const ActionIconButton = styled(IconButton)({
  padding: "3px",
});

export const ProcedureCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showActionsColumn",
})<{ showActionsColumn?: boolean }>(({ theme, showActionsColumn = true }) => ({
  display: "grid",
  gridTemplateColumns: getTableGridColumns(showActionsColumn),
  gap: "16px",
  alignItems: "flex-start",
  padding: "12px 16px",
  fontFamily: "Nunito",
  borderBottom: `1px solid ${colors.border.light}`,
  [theme.breakpoints.down("sm")]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "8px",
    padding: "12px",
  },
}));

export const AppointmentsTableHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showActionsColumn",
})<{ showActionsColumn?: boolean }>(({ theme, showActionsColumn = true }) => ({
  display: "grid",
  gridTemplateColumns: getTableGridColumns(showActionsColumn),
  gap: "16px",
  padding: "0 16px 12px",
  color: colors.text.secondary,
  fontFamily: "Nunito",
  fontSize: "14px",
  fontWeight: 700,
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

export const ProcedureTitle = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 500,
  color: colors.text.link,
  fontFamily: "Nunito",
  minWidth: 0,
  wordBreak: "break-word",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    paddingLeft: 0,
  },
}));

export const ProcedureMobileField = styled(Box)(({ theme }) => ({
  display: "contents",
  [theme.breakpoints.down("sm")]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    width: "100%",
  },
}));

export const ProcedureFieldLabel = styled(Typography)(({ theme }) => ({
  display: "none",
  fontSize: "12px",
  fontWeight: 700,
  color: colors.text.secondary,
  fontFamily: "Nunito",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    display: "block",
  },
}));

export const ProcedureCdtValue = styled(Typography)(({ theme }) => ({
  fontFamily: "Nunito",
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  fontSize: "14px",
  [theme.breakpoints.down("sm")]: {
    justifyContent: "flex-end",
    flex: 1,
    minWidth: 0,
  },
}));

export const CdtCode = styled(Box)(({ theme }) => ({
  fontWeight: 600,
  color: colors.text.action,
  fontFamily: "Nunito",
  fontSize: "14px",
  whiteSpace: "nowrap",
  padding: "0 8px",
  height: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: colors.background.lightBlue,
  borderRadius: "16px",
  border: `1px solid ${colors.text.action}`,
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
    height: "22px",
  },
}));

export const ProcedureFeeValue = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: colors.text.primary,
  fontFamily: "Nunito",
  fontSize: "14px",
  [theme.breakpoints.down("sm")]: {
    textAlign: "right",
    flex: 1,
  },
}));

export const AppointmentTimeframeRow = styled(Box)(({ theme }) => ({
  padding: "12px 16px 0px",
  display: "flex",
  minWidth: 0,
  "& > *": {
    margin: 0,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 12px 0",
  },
}));
